// Centralized image catalog. AI hero images live in /public/hero (generated
// via /app/backend/generate_hero_images.py). All other photos are direct
// Unsplash CDN URLs (free, hot-linkable, optimized via ?w=&q= params).

const u = (id, w = 1200, q = 75) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=${q}`;

// 4 cinematic hero visuals (AI-generated). Fallback to high-quality Unsplash
// during the brief window between deploy and first generation run.
export const HERO_SLIDES = [
  {
    src: "/hero/hero-1-boardroom.png",
    fallback: u("photo-1604328698692-f76ea9498e76", 1600), // boardroom
    title: "Executive strategy, made measurable",
    caption: "Operational excellence designed in the boardroom, executed on the floor.",
  },
  {
    src: "/hero/hero-2-factory.png",
    fallback: u("photo-1581094794329-c8112a89af12", 1600), // factory
    title: "From shop floor to bottom line",
    caption: "Lean manufacturing transformations with hard-dollar ROI.",
  },
  {
    src: "/hero/hero-3-warehouse.png",
    fallback: u("photo-1553413077-190dd305871c", 1600), // warehouse
    title: "Warehouses that move at the speed of demand",
    caption: "Slotting, accuracy, throughput — engineered end to end.",
  },
  {
    src: "/hero/hero-4-dashboard.png",
    fallback: u("photo-1551288049-bebda4e38f71", 1600), // dashboard
    title: "Decisions you can defend with data",
    caption: "Live KPI dashboards that turn signal into action.",
  },
];

// Environments we work in — gallery section
export const ENVIRONMENTS = [
  { t: "Executive Boardrooms", src: u("photo-1517502884422-41eaead166d4", 900) },
  { t: "Manufacturing Floors", src: u("photo-1581092918056-0c4c3acd3789", 900) },
  { t: "Warehouses & DCs",     src: u("photo-1586528116311-ad8dd3c8310d", 900) },
  { t: "Healthcare Facilities", src: u("photo-1551190822-a9333d879b1f", 900) },
  { t: "Retail Operations",     src: u("photo-1604719312566-8912e9227c6a", 900) },
  { t: "Distribution Networks", src: u("photo-1494412574643-ff11b0a5c1c3", 900) },
  { t: "Logistics & Fleet",     src: u("photo-1601584115197-04ecc0da31d7", 900) },
  { t: "Corporate Offices",     src: u("photo-1497366216548-37526070297c", 900) },
];

// Service photos (mapped by id from Solutions.jsx)
export const SERVICE_IMAGES = {
  "change-management":            u("photo-1551836022-deb4988cc6c0", 800),
  "continuous-improvement":       u("photo-1454165804606-c3d57bc86b40", 800),
  "process-mapping":              u("photo-1456324504439-367cee3b3c32", 800),
  "gap-analysis":                 u("photo-1542744173-8e7e53415bb0", 800),
  "bottleneck-identification":    u("photo-1521737711867-e3b97375f902", 800),
  "waste-reduction":              u("photo-1611273426858-450d8e3c9fce", 800),
  "sop-development":              u("photo-1450101499163-c8848c66ca85", 800),
  "kpi-development":              u("photo-1551288049-bebda4e38f71", 800),
  "contract-evaluation":          u("photo-1450101499163-c8848c66ca85", 800),
  "supply-chain":                 u("photo-1494412574643-ff11b0a5c1c3", 800),
  "warehouse":                    u("photo-1586528116311-ad8dd3c8310d", 800),
  "logistics":                    u("photo-1601584115197-04ecc0da31d7", 800),
  "bpr":                          u("photo-1518770660439-4636190af475", 800),
};

// Industry tile background photos
export const INDUSTRY_IMAGES = {
  "Manufacturing":         u("photo-1581092918056-0c4c3acd3789", 800),
  "Healthcare":            u("photo-1551190822-a9333d879b1f", 800),
  "Pharmaceuticals":       u("photo-1587854692152-cbe660dbde88", 800),
  "Retail":                u("photo-1604719312566-8912e9227c6a", 800),
  "Distribution":          u("photo-1494412574643-ff11b0a5c1c3", 800),
  "Warehousing":           u("photo-1586528116311-ad8dd3c8310d", 800),
  "Transportation":        u("photo-1436491865332-7a61a109cc05", 800),
  "Logistics":             u("photo-1601584115197-04ecc0da31d7", 800),
  "Government":            u("photo-1518181835702-6eef8b4b2113", 800),
  "Financial Services":    u("photo-1554224155-6726b3ff858f", 800),
  "Professional Services": u("photo-1497366216548-37526070297c", 800),
  "Real Estate":           u("photo-1486325212027-8081e485255e", 800),
};

// Testimonials (Unsplash portraits)
export const TESTIMONIALS = [
  {
    name: "Sarah Mitchell",
    title: "COO, Pacific Manufacturing",
    quote: "Clinivon mapped our entire operation in six weeks and cut order-to-ship cycle time by 41%. The team didn't just deliver a deck — they re-engineered how we run.",
    avatar: u("photo-1573496359142-b8d87734a5a2", 200),
  },
  {
    name: "David Okonkwo",
    title: "VP Supply Chain, Helix Logistics",
    quote: "Their bottleneck analysis paid for the engagement three times over in the first quarter. Genuinely the most pragmatic consultants I've worked with.",
    avatar: u("photo-1560250097-0b93528c311a", 200),
  },
  {
    name: "Dr. Priya Raman",
    title: "Director of Operations, Aurora Health",
    quote: "We finally have SOPs that nurses actually follow and KPIs leadership actually trusts. Compliance scores up 28% in two cycles.",
    avatar: u("photo-1580489944761-15a19d654956", 200),
  },
  {
    name: "Michael Chen",
    title: "CFO, Northstar Distribution",
    quote: "The contract review alone saved us $1.4M in unfavorable terms before we ever signed. Easy ROI conversation with the board.",
    avatar: u("photo-1507003211169-0a1dd7228f2d", 200),
  },
];

// Hero background video — public stock loop. Replace with /hero/clinivon-bg.mp4
// (drop your own MP4 into /app/frontend/public/hero/) once available.
export const HERO_BG_VIDEO = "https://videos.pexels.com/video-files/3209828/3209828-uhd_3840_2160_25fps.mp4";
